import { Body, Button, Spacing } from '@big-red-group/storefront-common-ui'
import { ExpandedOption } from './VoucherSelector'
import { GiftVoucherField, IndividualField, VoucherField } from '@big-red-group/storefront-common-checkout'
import Icon from './Icons'
import {
  IndividualMessages,
  IndividualMessageWrap,
  IndividualMessageWrapSession,
  IndividualMessageWrapIcon,
  ViewMessage,
  IndividualMessageForm,
  MessagePreview,
} from './IndividualMessage.styles'
import VoucherFieldsForm from './VoucherFieldsForm'
import { useState } from 'react'

type IndividualMessageProps = {
  individualMsgCounter: number
  setIndividualMsgCounter: (counter: number) => void
  allOptions: ExpandedOption[] | undefined
  giftQty: number
  individualFields: IndividualField[]
  setIndividualFields: (field: IndividualField[]) => void
  setFields: (fields: VoucherField[]) => void
  compileIndividualFields: (optionId: string, optionIndex: number, fields: VoucherField[]) => void
  giftVoucherFields: GiftVoucherField[]
  type: 'gift' | 'experience'
}

const IndividualMessage: React.FC<IndividualMessageProps> = ({
  individualMsgCounter,
  setIndividualMsgCounter,
  allOptions,
  giftQty,
  individualFields,
  setIndividualFields,
  setFields,
  compileIndividualFields,
  giftVoucherFields,
  type,
}) => {
  const [showCompletedMsgs, setShowCompletedMsgs] = useState<number[]>([])

  const getIndividualMessageIcon = (optionIndex: number) => {
    if (optionIndex === individualMsgCounter) {
      return 'pencil'
    } else if (isCompleted(optionIndex)) {
      return 'tick'
    } else {
      return 'ellipsis'
    }
  }

  const isCompleted = (optionIndex: number) => {
    const completedSection = individualFields.find((field) => field.optionIndex === optionIndex)
    return completedSection ? true : false
  }

  const getCompletedSection = (optionIndex: number) => {
    const completedSection = individualFields.find((field) => field.optionIndex === optionIndex)
    return completedSection
      ? completedSection!.formFields.reduce((acc, field) => ({ ...acc, ...field }), {})
      : undefined
  }

  const getHeaderName = (name: string, index: number) => {
    const defaultName = `Recipient ${index + 1} - ${name}`
    if (!individualFields.length) {
      return defaultName
    }

    const completedSection = getCompletedSection(index)
    return completedSection ? `Recipient ${index + 1} - ${completedSection!['TO']} (${name})` : defaultName
  }

  const editMessage = (index: number) => {
    setIndividualMsgCounter(index)
  }

  const renderMessageWrap = (totalLength: number, optionName: string, index: number, optionId: string) => {
    return (
      <IndividualMessageWrap firstItem={index === 0} lastItem={index === totalLength - 1} key={index}>
        <IndividualMessageWrapSession active={index === individualMsgCounter} complete={isCompleted(index)}>
          <IndividualMessageWrapIcon>
            <Icon name={getIndividualMessageIcon(index)} width={16} height={16} />
          </IndividualMessageWrapIcon>
          <Body weight="medium">{getHeaderName(optionName, index)}</Body>
          {isCompleted(index) && (
            <ViewMessage onClick={() => editMessage(index)}>
              <Body size="S" color="brand-secondary">
                Edit
              </Body>
            </ViewMessage>
          )}
        </IndividualMessageWrapSession>
        {index === individualMsgCounter && (
          <IndividualMessageForm>
            <VoucherFieldsForm
              setVoucherFields={setFields}
              compileIndividualFields={compileIndividualFields}
              setHasCompletedFieldsForm={() => {}}
              fields={giftVoucherFields.map((d) => ({
                ...d,
                value: individualFields[individualMsgCounter]?.formFields?.find((f) => f[d.key])?.[d.key] || d.value,
              }))}
              hasMultipleMessages
              optionId={optionId}
              optionIndex={index}
            />
          </IndividualMessageForm>
        )}
      </IndividualMessageWrap>
    )
  }

  return (
    <IndividualMessages>
      <Spacing size="S" />
      {type === 'experience' &&
        allOptions &&
        allOptions.map(({ id, name }, index) => renderMessageWrap(allOptions!.length, name, index, id.toString()))}
      {type === 'gift' &&
        giftQty &&
        [...Array(giftQty).keys()].map((qty, index) =>
          renderMessageWrap(giftQty, 'Gift voucher', index, qty.toString())
        )}
    </IndividualMessages>
  )
}
export default IndividualMessage
